import type { AbilityDemo } from '@/features/playbook/types';

const demo: AbilityDemo = {
  id: 'folk-ken',
  slug: 'folk-ken',
  label: 'Folk Ken',
  roll: {
    formula: 'Per 1 + Folk Ken 2 = 3 + die',
    niche: '+1 when sizing up common townsfolk',
    altPairing: 'Com 2 for reading someone over a sustained conversation = total 4',
  },
  whenToUse: [
    'Detect whether a merchant is being truthful about their wares.',
    "Gauge a farmer's emotional state—is he frightened, angry, or hiding something?",
    "Read a noblewoman's real intentions beneath polite small talk.",
    'Sense if someone is uncomfortable with a topic before they admit it.',
    'Determine whether a covenant visitor is genuinely trustworthy or merely playing a role.',
  ],
  samples: [
    `GM: "The village elder's face remains neutral as your covenant delegation enters the hall. This is Ex Astra business in spring 1195."
You (Mikaela): "I want to read what he truly thinks beneath that courtesy—is he concerned about Hermetic mages, or just protective of the spring?"
Elara: "We only seek knowledge."
GM: "Roll Per 1 + Folk Ken 2 = 3 + die. Niche townsfolk +1 = 4 total."

Teaches: Your Curious trait (+3) compels you to understand what lies beneath the surface.`,
    `GM: "The farmer who offered water earlier now avoids your eyes when Brother Alfric mentions your order's interest in local medicinal springs. It is spring 1195, Ex Astra covenant business."
You (Mikaela): "My Curious trait won't let this go. I'm reading him carefully—kindness or hidden motive? Is he a threat to our work here?"
Sir Helgi: "The man seems simple enough."
GM: "Roll Per 1 + Folk Ken 2 = 3 + die. Niche townsfolk +1 = 4 total."

Teaches: Your Curious trait (+3) drives you to push past surface courtesy into deeper truth.`,
    `GM: "The innkeeper's daughter lingers near the fire as your group discusses gathering ingredients. He watches from the bar, his jaw tight. This is the Not-Quite-Free Real Estate sidequest. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
You (Mikaela): "I need to know if he suspects what we really are. My Curious won't rest—what does he truly fear about strangers asking herb questions?"
GM: "He shifts his weight."
GM: "Roll Per 1 + Folk Ken 2 = 3 + die. Niche townsfolk +1 = 4 total."

Teaches: Mikaela's Clerk virtue makes her attentive to details others miss—the innkeeper's tell is written in his body.`,
    `GM: "A priest approaches your camp near the spring. He speaks of bandits on the western road and offers to guide you. His voice is steady, but something feels off. It is Ex Astra territory, spring 1195."
You (Mikaela): "I'm gauging him—his real background, what he wants from us. Does he genuinely fear bandits, or is he scouting? My Humble flaw doubts my own read, but I have to try."
Brother Alfric: "A fellow servant of faith is always welcome."
GM: "Roll Per 1 + Folk Ken 2 = 3 + die. Niche townsfolk +1 = 4 total."

Teaches: Your Humble flaw makes you doubt your instincts even when they are correct—a cost of your deference.`,
    `GM: "The merchant of rare botanicals becomes visibly nervous when Corvus inquires about spring plants. He prices items high, then low, contradicting himself. This is the Not-Quite-Free Real Estate sidequest. [Ex Astra scouting, spring 1195, "Not-Quite-Free Real Estate" sidequest.]"
You (Mikaela): "My Curious trait pulls me forward. I'm reading him deeply—is he nervous because he knows who we are, or does he just see profit in our desperation? Does he know the spring's true nature?"
GM: "His hands tremble slightly."
GM: "Roll Per 1 + Folk Ken 2 = 3 + die. Niche townsfolk +1 = 4 total."

Teaches: Your Curious trait (+3) refuses to accept the merchant's explanations at face value.`,
  ],
  vignette: "The merchant insists the lamp oil is pure, but you notice his fingers drumming—a tell you've learned from years in the covenant studying people as closely as manuscripts. When you lean forward with interest and ask about his family, his shoulders relax. He admits the batch is mixed; his supplier is struggling.",
  gotcha: "Folk Ken reads what someone truly wants, fears, or values beneath their words and bearing. Awareness catches what's physically hidden in a room or landscape. Use Folk Ken when you need to understand someone's real motives and character; use Awareness when you need to spot a concealed object or notice environmental danger.",
};

export default demo;
